import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, CheckCircle2, XCircle, AlertCircle, Eye, Loader2, RefreshCw, Search } from "lucide-react";

const STATUS_META = {
  pending:  { c: "bg-amber-100 text-amber-900",     l: "Pending",  Icon: AlertCircle },
  verified: { c: "bg-emerald-100 text-emerald-800", l: "Verified", Icon: CheckCircle2 },
  rejected: { c: "bg-red-100 text-red-800",         l: "Rejected", Icon: XCircle },
};

const fmtIDR = (n) => `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(n || 0))}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

function PaymentRow({ b, onChange }) {
  const [busy, setBusy] = React.useState(null);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [amt, setAmt] = React.useState(b.payment_proof_amount_idr || "");

  const status = b.payment_proof_status || "pending";
  const meta = STATUS_META[status];

  const view = async () => {
    setBusy("view");
    try {
      const r = await api.get(`/files/${b.payment_proof_url}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch {
      toast.error("Gagal preview");
    } finally { setBusy(null); }
  };

  const act = async (action, extra = {}) => {
    setBusy(action);
    try {
      const payload = { action, ...extra };
      const r = await api.patch(`/admin/bookings/${b.id}/payment-proof/verify`, payload);
      toast.success(action === "verify" ? "Pembayaran diverifikasi" : "Pembayaran ditolak");
      setRejectOpen(false); setReason("");
      onChange(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setBusy(null); }
  };

  const MetaIcon = meta.Icon;

  return (
    <tr className="border-t border-border align-top" data-testid={`payment-row-${b.id}`}>
      <td className="p-4">
        <div className="font-medium text-primary">{b.name}</div>
        <div className="text-xs text-muted-foreground">{b.email}</div>
        {b.booking_number && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{b.booking_number}</div>}
      </td>
      <td className="p-4">
        <div className="text-sm">{b.package_name}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.status}</div>
      </td>
      <td className="p-4 text-xs">
        <div><strong>Jumlah:</strong> {b.payment_proof_amount_idr ? fmtIDR(b.payment_proof_amount_idr) : "—"}</div>
        <div><strong>Ref:</strong> {b.payment_proof_ref || "—"}</div>
        <div className="text-muted-foreground mt-1">{b.payment_method || "—"}</div>
      </td>
      <td className="p-4">
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${meta.c}`} data-testid={`payment-status-${b.id}`}>
          <MetaIcon className="h-3 w-3" /> {meta.l}
        </span>
        {status === "rejected" && b.payment_proof_reject_reason && (
          <div className="text-[10px] text-red-800 mt-1.5">{b.payment_proof_reject_reason}</div>
        )}
      </td>
      <td className="p-4">
        <div className="flex flex-wrap gap-1.5">
          <button onClick={view} disabled={busy === "view"} className="text-[10px] px-2.5 py-1.5 rounded-full border border-border hover:bg-muted inline-flex items-center gap-1" data-testid={`payment-view-${b.id}`}>
            {busy === "view" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
            Lihat
          </button>
          {status !== "verified" && (
            <>
              <button
                onClick={() => act("verify", amt ? { dp_amount_idr: parseInt(amt, 10) } : {})}
                disabled={busy === "verify"}
                className="text-[10px] px-2.5 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                data-testid={`payment-verify-${b.id}`}
              >
                {busy === "verify" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                Verify
              </button>
              <button
                onClick={() => setRejectOpen((v) => !v)}
                disabled={busy === "reject"}
                className="text-[10px] px-2.5 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1"
                data-testid={`payment-reject-${b.id}`}
              >
                <XCircle className="h-3 w-3" /> Tolak
              </button>
            </>
          )}
          {status !== "verified" && (
            <input
              placeholder="Set DP (Rp)"
              value={amt}
              onChange={(e) => setAmt(e.target.value.replace(/\D/g, ""))}
              className="text-[10px] w-24 px-2 py-1.5 rounded border border-border"
              data-testid={`payment-dp-${b.id}`}
            />
          )}
        </div>
        {rejectOpen && (
          <div className="mt-2 space-y-1.5">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Alasan penolakan (wajib)"
              className="w-full text-xs p-2 border border-red-200 rounded"
              rows={2}
              data-testid={`payment-reject-reason-${b.id}`}
            />
            <button
              onClick={() => act("reject", { reason })}
              disabled={!reason.trim() || busy === "reject"}
              className="text-[10px] px-2.5 py-1.5 rounded-full bg-red-700 text-white hover:bg-red-800 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`payment-reject-confirm-${b.id}`}
            >
              {busy === "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              Konfirmasi Tolak
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function PaymentsTab() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState("pending");
  const [q, setQ] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings");
      // Only bookings with payment_proof_url
      setRows(r.data.filter((b) => b.payment_proof_url));
    } catch {
      toast.error("Gagal memuat pembayaran");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const updateRow = (updated) =>
    setRows((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));

  const filtered = React.useMemo(() => {
    let out = rows;
    if (filter !== "all") out = out.filter((b) => (b.payment_proof_status || "pending") === filter);
    if (q.trim()) {
      const qq = q.toLowerCase();
      out = out.filter((b) => [b.name, b.email, b.package_name, b.booking_number, b.payment_proof_ref].filter(Boolean).some((s) => String(s).toLowerCase().includes(qq)));
    }
    return out;
  }, [rows, filter, q]);

  const counts = React.useMemo(() => ({
    all: rows.length,
    pending: rows.filter((b) => (b.payment_proof_status || "pending") === "pending").length,
    verified: rows.filter((b) => b.payment_proof_status === "verified").length,
    rejected: rows.filter((b) => b.payment_proof_status === "rejected").length,
  }), [rows]);

  const revenue = React.useMemo(() => {
    return rows
      .filter((b) => b.payment_proof_status === "verified")
      .reduce((sum, b) => sum + (b.payment_proof_amount_idr || b.dp_amount_idr || 0), 0);
  }, [rows]);

  const FilterTab = ({ v, label, cls, count, active, onClick }) => (
    <button
      onClick={onClick}
      data-testid={`payment-filter-${v}`}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium ${cls} ${active ? "ring-2 ring-offset-1 ring-accent" : ""}`}
    >
      {label}
      <span className="min-w-[22px] text-center rounded-full bg-white/70 border border-current/20 px-1.5 py-0.5 text-[10px]">{count || 0}</span>
    </button>
  );

  return (
    <TabsContent value="payments" data-testid="tab-payments-content">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-primary flex items-center gap-2">
            <Wallet className="h-5 w-5 text-accent" /> Verifikasi Pembayaran
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Bukti transfer manual dari jamaah. Set DP amount saat verify untuk update status pembayaran.</p>
          <p className="text-xs mt-2">
            Total pemasukan terverifikasi: <span className="font-serif text-primary font-semibold">{fmtIDR(revenue)}</span>
          </p>
        </div>
        <button onClick={load} disabled={loading} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border hover:bg-muted">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <FilterTab v="all" label="Semua" cls="border-primary/30 text-primary bg-white hover:bg-muted" count={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
        <FilterTab v="pending" label="Menunggu" cls="border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100" count={counts.pending} active={filter === "pending"} onClick={() => setFilter("pending")} />
        <FilterTab v="verified" label="Terverifikasi" cls="border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100" count={counts.verified} active={filter === "verified"} onClick={() => setFilter("verified")} />
        <FilterTab v="rejected" label="Ditolak" cls="border-red-200 text-red-800 bg-red-50 hover:bg-red-100" count={counts.rejected} active={filter === "rejected"} onClick={() => setFilter("rejected")} />
        <div className="ml-auto relative">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama/email/booking..."
            data-testid="payments-search"
            className="pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-background w-64 focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="p-4 font-medium">Jamaah</th>
                <th className="p-4 font-medium">Paket</th>
                <th className="p-4 font-medium">Detail Transfer</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="p-10 text-center text-muted-foreground">Tidak ada pembayaran di filter ini.</td></tr>
              )}
              {filtered.map((b) => (
                <PaymentRow key={b.id} b={b} onChange={updateRow} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </TabsContent>
  );
}
